import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';
import { describePoint, locatePoint, OVERLAY_ATTR } from './cursorAnchor';

// How often our own cursor is sent (ms); the others' cursors glide between updates
const SEND_EVERY_MS = 50;
// A cursor that hasn't moved for this long fades out
const STALE_AFTER_MS = 20000;

const COLOURS = ['#2563eb', '#16a34a', '#db2777', '#9333ea', '#0891b2', '#ea580c', '#4f46e5', '#ca8a04'];
export const colourFor = id => {
  const hash = String(id)
    .split('')
    .reduce((acc, c) => c.charCodeAt(0) + ((acc << 5) - acc), 0);
  return COLOURS[Math.abs(hash) % COLOURS.length];
};

/*
 * CURSORS_V2: the whole page, not just the left card. Each cursor is sent as the path of
 * elements under the pointer (see cursorAnchor.js), so it lands on the same thing for everyone
 * whatever their screen width, sidebar, collapsed sections or scroll position.
 *
 * Senders on the first version only send { region, x, y } for the left card / tab pane, so
 * those are still placed the old way, and we still send those fields for them.
 */
const regionAt = target => target?.closest?.('[data-cursor-region]') || null;
const regionEl = name => document.querySelector(`[data-cursor-region="${name}"]`);

const legacyFields = (target, x, y) => {
  const el = regionAt(target);
  if (!el) return { region: null };
  const rect = el.getBoundingClientRect();
  return {
    region: el.dataset.cursorRegion,
    x: (x - rect.left) / rect.width,
    y: y - rect.top + el.scrollTop
  };
};

const locateLegacy = (c, viewingTab) => {
  if (!c.region) return null;
  if (c.region === 'tab' && c.tab !== (viewingTab || null)) return null;
  const el = regionEl(c.region);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const left = rect.left + c.x * rect.width;
  const top = rect.top + c.y - el.scrollTop;
  if (top < rect.top - 4 || top > rect.bottom || left < rect.left - 4 || left > rect.right) {
    return null;
  }
  return { left, top, exact: true };
};

const EDGE_ARROW = { top: ArrowUp, bottom: ArrowDown, left: ArrowLeft, right: ArrowRight };
// The edge pill sits inside the visible area, against the side the cursor is past
const EDGE_PILL = {
  top: 'translate(-50%, 0)',
  bottom: 'translate(-50%, -100%)',
  left: 'translate(0, -50%)',
  right: 'translate(-100%, -50%)'
};
// A small nudge towards where they are (none for people who turn motion off)
const EDGE_CSS = `
@keyframes cursor-nudge-top { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-2px) } }
@keyframes cursor-nudge-bottom { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(2px) } }
@keyframes cursor-nudge-left { 0%, 100% { transform: translateX(0) } 50% { transform: translateX(-2px) } }
@keyframes cursor-nudge-right { 0%, 100% { transform: translateX(0) } 50% { transform: translateX(2px) } }
@media (prefers-reduced-motion: no-preference) {
  [data-cursor-arrow] { animation: 1.4s ease-in-out infinite; }
  [data-cursor-arrow=top] { animation-name: cursor-nudge-top; }
  [data-cursor-arrow=bottom] { animation-name: cursor-nudge-bottom; }
  [data-cursor-arrow=left] { animation-name: cursor-nudge-left; }
  [data-cursor-arrow=right] { animation-name: cursor-nudge-right; }
}
`;

// How quickly a cursor catches up with where it should be (ms, the time constant of the glide):
// updates arrive every SEND_EVERY_MS, so this keeps it moving between them instead of hopping
const GLIDE_MS = 70;
// Further than this in one go (a jump to another part of the page) it moves straight there
const SNAP_PX = 700;

/**
 * Moves its children to (left, top) smoothly, one frame at a time, outside React renders.
 * The frames stop once it has arrived and start again when the spot changes.
 */
const Glide = ({ id, left, top, className = '', children, ...rest }) => {
  const ref = useRef(null);
  const at = useRef(null);
  const target = useRef({ left, top });
  const frame = useRef(0);
  target.current = { left, top };

  const draw = () => {
    if (ref.current && at.current) {
      ref.current.style.transform = `translate3d(${at.current.left}px, ${at.current.top}px, 0)`;
    }
  };

  useEffect(() => {
    if (!at.current || Math.hypot(left - at.current.left, top - at.current.top) > SNAP_PX) {
      at.current = { left, top };
      draw();
      return;
    }
    if (frame.current) return;
    let last = performance.now();
    const step = now => {
      const dt = Math.min(now - last, 50);
      last = now;
      const t = target.current;
      const p = at.current;
      const k = 1 - Math.exp(-dt / GLIDE_MS);
      p.left += (t.left - p.left) * k;
      p.top += (t.top - p.top) * k;
      if (Math.abs(t.left - p.left) < 0.3 && Math.abs(t.top - p.top) < 0.3) {
        p.left = t.left;
        p.top = t.top;
        frame.current = 0;
      } else {
        frame.current = requestAnimationFrame(step);
      }
      draw();
    };
    frame.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, top]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  const start = at.current || { left, top };
  return (
    <div
      ref={ref}
      data-cursor-id={id}
      // Where it's heading (the drawn spot can still be gliding there)
      data-x={left}
      data-y={top}
      className={`absolute left-0 top-0 will-change-transform ${className}`}
      style={{ transform: `translate3d(${start.left}px, ${start.top}px, 0)` }}
      {...rest}
    >
      {children}
    </div>
  );
};

/**
 * Live cursors of the other people on this record (Figma style), over a presence channel.
 * - channelRef: the joined Realtime channel (RecordViewers owns it)
 * - onCursor: RecordViewers' ref that receives incoming 'cursor' broadcasts
 * - others: the other viewers (from presence), used for names and to drop people who left
 * - viewingTab: our tab's label (sent along so the first version can still filter on it)
 */
const LiveCursors = ({ channelRef, onCursor, me, others, viewingTab }) => {
  const [cursors, setCursors] = useState({});
  const [, setFrame] = useState(0);
  const lastSent = useRef(0);
  const pending = useRef(null);
  const sendTimer = useRef(null);
  const pointer = useRef(null);
  const tabRef = useRef(viewingTab);
  tabRef.current = viewingTab;
  const anyoneElse = others.length > 0;

  // Receive
  useEffect(() => {
    onCursor.current = payload => {
      if (!payload || String(payload.id) === String(me)) return;
      setCursors(prev => ({ ...prev, [payload.id]: { ...payload, at: Date.now() } }));
    };
    return () => {
      onCursor.current = null;
    };
  }, [onCursor, me]);

  // Send (only while someone else is here)
  useEffect(() => {
    if (!anyoneElse) return undefined;

    const send = payload => {
      const channel = channelRef.current;
      if (!channel || channel.state !== 'joined') return;
      channel.send({ type: 'broadcast', event: 'cursor', payload });
      lastSent.current = Date.now();
    };
    // Worked out when it's sent, not on every move: the latest pointer wins
    const build = () => {
      const p = pointer.current;
      if (!p) return { id: me, region: null, off: true };
      // After a scroll or click the pointer may be over something else without moving
      const target = p.fresh ? p.target : document.elementFromPoint(p.x, p.y);
      p.fresh = false;
      const described = describePoint(target, p.x, p.y);
      if (!described || described.off) return { id: me, region: null, off: true };
      return {
        id: me,
        tab: tabRef.current || null,
        ...legacyFields(target, p.x, p.y),
        ...described
      };
    };
    const queue = () => {
      pending.current = true;
      const wait = SEND_EVERY_MS - (Date.now() - lastSent.current);
      if (wait <= 0) {
        pending.current = false;
        send(build());
      } else if (!sendTimer.current) {
        sendTimer.current = setTimeout(() => {
          sendTimer.current = null;
          if (pending.current) send(build());
          pending.current = false;
        }, wait);
      }
    };

    const onMove = e => {
      pointer.current = { x: e.clientX, y: e.clientY, target: e.target, fresh: true };
      queue();
    };
    const onShift = () => {
      // After the page has laid itself out again (a timer: frames pause in background tabs)
      if (pointer.current) setTimeout(queue, 0);
    };
    const onLeave = () => {
      pointer.current = null;
      queue();
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onShift, { passive: true, capture: true });
    window.addEventListener('click', onShift, { capture: true });
    document.documentElement.addEventListener('pointerleave', onLeave);
    window.addEventListener('blur', onLeave);
    return () => {
      clearTimeout(sendTimer.current);
      sendTimer.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', onShift, { capture: true });
      window.removeEventListener('click', onShift, { capture: true });
      document.documentElement.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('blur', onLeave);
      send({ id: me, region: null, off: true });
    };
  }, [anyoneElse, channelRef, me]);

  // Re-place the cursors whenever our page moves: scrolling, resizing, sections opening and
  // closing, content loading in. Batched to one placement per frame.
  useEffect(() => {
    if (!anyoneElse) return undefined;
    let frame = 0;
    const bump = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setFrame(f => f + 1);
      });
    };
    window.addEventListener('scroll', bump, { passive: true, capture: true });
    window.addEventListener('resize', bump);
    window.addEventListener('transitionend', bump, true);
    const root = document.getElementById('root') || document.body;
    const observer = new MutationObserver(bump);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null;
    resize?.observe(root);
    const timer = setInterval(bump, 5000);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', bump, { capture: true });
      window.removeEventListener('resize', bump);
      window.removeEventListener('transitionend', bump, true);
      observer.disconnect();
      resize?.disconnect();
      clearInterval(timer);
    };
  }, [anyoneElse]);

  if (!anyoneElse) return null;

  const people = new Map(others.map(o => [String(o.id), o]));
  const now = Date.now();

  const placed = Object.values(cursors)
    .map(c => {
      const person = people.get(String(c.id));
      if (!person || c.off || person.state === 'away') return null;
      if (now - c.at > STALE_AFTER_MS) return null;
      let spot = null;
      try {
        spot = c.steps ? locatePoint(c) : locateLegacy(c, viewingTab);
      } catch {
        spot = null;
      }
      return spot ? { id: c.id, person, ...spot } : null;
    })
    .filter(Boolean);

  return createPortal(
    <div
      {...{ [OVERLAY_ATTR]: '' }}
      className='pointer-events-none fixed inset-0 z-[60]'
      aria-hidden='true'
    >
      {placed.some(p => p.edge) && <style>{EDGE_CSS}</style>}
      {placed.map(({ id, left, top, person, exact, edge, tab }) => {
        const colour = colourFor(id);
        const name = person.firstName || 'Someone';
        const label = tab ? `${name} · ${tab}` : name;
        if (edge) {
          // Somewhere we've scrolled away from: a pill on that edge pointing the way
          const Arrow = EDGE_ARROW[edge];
          const arrow = (
            <span
              data-cursor-arrow={edge}
              className='flex h-4 w-4 flex-none items-center justify-center rounded-full bg-white/25'
            >
              <Arrow size={11} strokeWidth={2.75} />
            </span>
          );
          return (
            <Glide key={id} id={id} left={left} top={top} data-cursor-edge={edge}>
              <span
                className='flex items-center gap-1.5 whitespace-nowrap rounded-full py-1 text-[11px] font-medium leading-none text-white shadow-lg shadow-base-900/20 ring-2 ring-white'
                style={{
                  backgroundColor: colour,
                  transform: EDGE_PILL[edge],
                  paddingLeft: edge === 'right' ? 10 : 4,
                  paddingRight: edge === 'right' ? 4 : 10
                }}
              >
                {edge !== 'right' && arrow}
                {label}
                {edge === 'right' && arrow}
              </span>
            </Glide>
          );
        }
        return (
          <Glide
            key={id}
            id={id}
            left={left}
            top={top}
            data-cursor-exact={exact ? 'true' : 'false'}
            className={`transition-opacity duration-200 ${exact ? 'opacity-100' : 'opacity-60'}`}
          >
            <svg width='18' height='18' viewBox='0 0 24 24' className='-translate-x-[3px] -translate-y-[2px] drop-shadow-sm'>
              <path
                d='M4 2.5 20 10l-7 2.2L10.3 19z'
                fill={colour}
                stroke='white'
                strokeWidth='1.5'
                strokeLinejoin='round'
              />
            </svg>
            <span
              className='absolute left-3.5 top-4 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium text-white shadow-sm'
              style={{ backgroundColor: colour }}
            >
              {label}
            </span>
          </Glide>
        );
      })}
    </div>,
    document.body
  );
};

export default LiveCursors;
